"use client";

import Image from "next/image";
import Link from "next/link";
import { useSelectedLayoutSegments } from "next/navigation";
import { ReactNode } from "react";

import { useSessionProfile } from "@saas/account/feature/@hook/session-profile/use-session-profile";
import { useLoyaltyBrandContext } from "@saas/loyalty/feature/@context/loyalty-brand/loyalty-brand.context";
import { useEmailVerificationStatus } from "@saas/loyalty/feature/@hook/email-verification-status/use-email-verification-status";
import { UnverifiedPhoneNotificationBar } from "@saas/loyalty/ui/unverified-phone-notification-bar/unverified-phone-notification-bar";
import { createLoyaltyUrl } from "@saas/loyalty/utils/create-loyalty-url/create-loyalty-url";
import { UserIcon } from "@saas/shared/icon";
import { IconButton } from "@saas/shared/ui/icon-button/icon-button";

import { LoyaltyRouteHeader } from "../loyalty-route-header/loyalty-route-header";
import Background from "./assets/bg-gradient.svg";
import Logo from "./assets/logo.svg";

export interface ILoyaltyLayoutProps {
  children: ReactNode;
  testid?: string;
}

export const LoyaltyLayout = ({
  children,
  testid = "loyalty-app",
}: ILoyaltyLayoutProps) => {
  const { brandName, logoUrl, brandSlug } = useLoyaltyBrandContext();
  const { profile } = useSessionProfile();

  useEmailVerificationStatus();

  const segments = useSelectedLayoutSegments();
  const homepageSegment = !segments.length;
  const currentSegmentPath = segments.join("/");

  const accountUrl = createLoyaltyUrl({
    brandSlug: brandSlug,
    pathname: "/account",
  });

  const homepageUrl = createLoyaltyUrl({
    brandSlug: brandSlug,
    pathname: "/",
  });

  const segmentRecord: Record<string, { label: string; backUrl: string }> = {
    "account": {
      label: "Akun",
      backUrl: homepageUrl,
    },
    "account/update-profile": {
      label: "Ubah Profil Pengguna",
      backUrl: accountUrl,
    },
    "account/update-email": {
      label: "Akun",
      backUrl: accountUrl,
    },
    "account/update-email/verify": {
      label: "Akun",
      backUrl: accountUrl,
    },
    "account/change-password": {
      label: "Ubah Kata Sandi",
      backUrl: accountUrl,
    },
    "earn-points": {
      label: "Peroleh Poin",
      backUrl: homepageUrl,
    },
    "point-history": {
      label: "Riwayat Poin",
      backUrl: homepageUrl,
    },
  };

  const unverifiedNotificationSegments = ["", "account"];
  const showUnverifiedNotification =
    !profile?.whatsappVerified &&
    unverifiedNotificationSegments.includes(currentSegmentPath);

  return (
    <>
      {/* Fixed Background */}
      <div className={"fixed bottom-0 z-[-1] h-[70dvh] w-full"}>
        <Image
          alt={"Gradient Background"}
          src={Background}
          fill={true}
          className={"object-cover object-bottom"}
        />
      </div>

      <main
        className={
          "mx-auto flex min-h-full flex-col gap-2 md:max-w-[482px] md:gap-4"
        }
      >
        {homepageSegment ? (
          <header className={"min-h-[3.5rem] py-4 px-5"}>
            <div
              className={"flex h-6 items-center justify-between gap-4 md:h-10"}
            >
              <Image
                alt={`${brandName} logo`}
                className={"object-contain"}
                src={logoUrl}
                width={120}
                height={20}
              />

              <Link href={accountUrl}>
                <IconButton
                  className={"text-button rounded-full"}
                  icon={UserIcon}
                  size={"big"}
                  label={"Lihat Akun"}
                  variant={"tertiary"}
                  testid={testid}
                />
              </Link>
            </div>
          </header>
        ) : null}

        {currentSegmentPath && segmentRecord[currentSegmentPath] ? (
          <LoyaltyRouteHeader
            label={segmentRecord[currentSegmentPath].label}
            backUrl={segmentRecord[currentSegmentPath].backUrl}
            testid={testid}
          />
        ) : null}

        <div
          className={
            "mx-auto h-full min-h-[calc(100dvh-137px)] w-full overflow-y-auto px-4 pb-8"
          }
        >
          {showUnverifiedNotification ? (
            <UnverifiedPhoneNotificationBar
              segment={currentSegmentPath}
              testid={testid}
            />
          ) : null}

          {children}
        </div>

        {/* TODO: Handle gradient background once get assets from PD */}
        <footer className={"flex min-h-[2rem] items-center justify-center"}>
          <div className={"flex items-center justify-center gap-1"}>
            <p className={"text-le body-b3"}>Powered by</p>
            <div className={"mb-[3px] flex items-start"}>
              <Image alt={"Melaka logo"} src={Logo} width={46} height={17} />
            </div>
          </div>
        </footer>
      </main>
    </>
  );
};
